import { Box, SvgIcon, Typography } from "@mui/material";
import EmptyState from "global/components/EmptyState/EmptyState";
import strings from "global/constants/StringConstants";
import styles from "../ContractSearch.styles";
import t from "../ContractSearch.tokens";
import { fill } from "../ContractSearchHelper";
import { getContractSearchIcon } from "utils/DrawerIcons";
import PromptChip from "./PromptChip";

interface ContractSearchEmptyStateProps {
  totalIndexed: number;
  /** False when the documents list failed to load: show the welcome block, not "nothing to search". */
  countKnown?: boolean;
  onAsk: (text: string) => void;
  disabled?: boolean;
}

/**
 * What the thread shows before the first question. With documents: the
 * design's welcome block and starter chips. Without: the app's shared
 * `EmptyState` — nothing to search yet, no chips.
 */
const ContractSearchEmptyState = ({
  totalIndexed,
  countKnown = true,
  onAsk,
  disabled,
}: ContractSearchEmptyStateProps) => {
  const copy = strings.contractSearch;

  if (countKnown && totalIndexed === 0) {
    return (
      <EmptyState
        size="full"
        icon="search"
        accent="brand"
        title={copy.noDocumentsTitle}
        body={copy.noDocumentsBody}
        framed
      />
    );
  }

  return (
    <Box sx={styles.emptyWrap}>
      {/* The same glyph as the "Contract Search" item in the sidebar. */}
      <Box sx={styles.emptyIconTile} aria-hidden="true">
        <SvgIcon sx={{ fontSize: t.empty.iconSize, display: "block" }}>
          {getContractSearchIcon(t.empty.iconColor)}
        </SvgIcon>
      </Box>
      <Typography component="h2" sx={styles.emptyTitle}>
        {copy.emptyTitle}
      </Typography>
      <Typography component="p" sx={styles.emptyBody}>
        {countKnown && totalIndexed > 0
          ? fill(copy.emptyBody, { count: totalIndexed })
          : copy.emptyBodyUnknown}
      </Typography>
      <Box sx={styles.chipRow} role="group" aria-label="Suggested questions">
        {copy.starterPrompts.map((prompt) => (
          <PromptChip key={prompt} text={prompt} onSelect={onAsk} disabled={disabled} />
        ))}
      </Box>
    </Box>
  );
};

export default ContractSearchEmptyState;
